import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';

import { Icon } from '@column/column-ui-kit';
import { DataTable } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { Empty as TableEmpty, tableDefaults, useTimeFormat } from '~/components/Table';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardTransactionRepository } from '~/repositories/CardTransactionRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';

import { CardTransactionRow, getCardTransactionColumns } from './shared';

export const PageCardTransactionsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-transactions');

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['card-transactions', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () =>
      CardTransactionRepository.listCardTransactions({
        limit: pageSize,
        startingAfter: cursor,
      }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const transactions = data?.cardTransactions ?? [];
  const hasMore = Boolean(data?.hasMore);

  return (
    <>
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<CardTransactionRow>
            {...tableDefaults}
            columns={getCardTransactionColumns(timeFormat, onTimeFormatChange)}
            data={isError ? [] : transactions}
            rowKey={(row) => row.id}
            isLoading={isFetching}
            emptyContent={
              isError ? (
                <TableEmpty icon={<Icon.Warning />} title="Failed to load card transactions" />
              ) : (
                <TableEmpty
                  icon={<Icon.AnimationMoney />}
                  title="No card transactions found"
                  description="Card transactions will appear here once cards are used."
                />
              )
            }
            pagination={{
              hasNext: hasMore,
              hasPrevious,
              pageIndex,
              pageSize,
              onNext: () => {
                if (transactions.length > 0) {
                  onNext({ cursor: transactions[transactions.length - 1].id });
                }
              },
              onPrevious,
              onPageSizeChange: setPageSize,
            }}
            onRowClick={(row) => {
              navigate(`${ROUTE.CARD_TRANSACTIONS}/edit/${row.id}`);
            }}
          />
        </Inner>
      </Loading>
    </>
  );
};
